import React, { useMemo } from 'react';
import { buildCleanupSuggestions, type CleanupSuggestion } from '../../utils/importIntelligence';
import type { ParsedModification } from './importReviewTypes';

interface ImportCleanupSuggestionsProps {
  modifications: ParsedModification[];
  excludedIndices: Set<number>;
  availableCategories: string[];
  availableVendors: string[];
  onAction: (suggestion: CleanupSuggestion) => void;
}

export const ImportCleanupSuggestions: React.FC<ImportCleanupSuggestionsProps> = ({
  modifications,
  excludedIndices,
  availableCategories,
  availableVendors,
  onAction,
}) => {
  const suggestions = useMemo(
    () =>
      buildCleanupSuggestions(
        modifications,
        excludedIndices,
        availableCategories,
        availableVendors
      ),
    [modifications, excludedIndices, availableCategories, availableVendors]
  );

  if (suggestions.length === 0) return null;

  return (
    <section className="rounded-lg border border-white/[0.10] bg-[#141414]/40 px-4 py-3">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
        Suggested cleanup
      </p>
      <ul className="space-y-2">
        {suggestions.map((suggestion) => (
          <li
            key={suggestion.id}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm"
          >
            <span className="text-gray-300">{suggestion.message}</span>
            {suggestion.actionLabel && suggestion.action && (
              <button
                type="button"
                onClick={() => onAction(suggestion)}
                className="self-start sm:self-auto text-xs px-2.5 py-1.5 rounded border border-orange-600/50 text-orange-300 hover:bg-orange-900/20 whitespace-nowrap"
              >
                {suggestion.actionLabel}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};
